import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../lib/components/custom/Icon";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import { Input } from "../../lib/components/ui/input";
import { Label } from "../../lib/components/ui/label";
import { useApi } from "../../lib/hooks/api/use-api";
import Layout from "../layout";

export default function ForgotPassword() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { auth } = useApi();
	const { data: session } = auth.useSession();

	// Redirect authenticated users to profile page
	useEffect(() => {
		if (session?.user) {
			navigate({ to: "/profile" });
		}
	}, [session, navigate]);
	const [isLoading, setIsLoading] = useState(false);
	const [email, setEmail] = useState("");
	const [error, setError] = useState("");
	const [success, setSuccess] = useState(false);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value);
		if (error) setError(""); // Clear error on input change
	};

	const handleForgotPassword = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setIsLoading(true);

		// Basic validation
		if (!email.trim()) {
			setError(t("auth.signin.errors.email_required"));
			setIsLoading(false);
			return;
		}

		if (!/\S+@\S+\.\S+/.test(email)) {
			setError(t("auth.forgot_password.errors.invalid_email"));
			setIsLoading(false);
			return;
		}

		try {
			const response = await auth.requestPasswordReset({
				email: email,
				redirectTo: "/auth/reset-password",
			});

			if (response.error) {
				setError(
					response.error.message ||
						t("auth.forgot_password.errors.send_failed"),
				);
			} else {
				setSuccess(true);
			}
		} catch (err) {
			setError(t("auth.signin.errors.unexpected"));
			console.error("Forgot password error:", err);
		} finally {
			setIsLoading(false);
		}
	};

	if (success) {
		return (
			<Layout>
				<div className="p-8 border min-h-full rounded-large bg-gradient-main">
					<div className="max-w-md mx-auto">
						{/* Header */}
						<div className="text-center mb-8">
							<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<Icon name="CircleCheck" className="w-8 h-8 text-green-600" />
							</div>
							<h1 className="text-3xl font-manrope font-semibold mb-2">
								{t("auth.forgot_password.check_email_header")}
							</h1>
							<p className="text-muted-foreground">
								{t("auth.forgot_password.sent_link", { email })}
							</p>
						</div>

						{/* Success Card */}
						<Card className="p-6">
							<div className="text-center space-y-4">
								<p className="text-sm text-muted-foreground">
									{t("auth.forgot_password.expiry_notice")}
								</p>

								<div className="space-y-3">
									<Button asChild className="w-full">
										<Link to="/auth/signin">
											<Icon name="LogIn" className="w-4 h-4 mr-2" />
											{t("auth.forgot_password.back_to_signin")}
										</Link>
									</Button>

									<Button
										variant="ghost"
										onClick={() => setSuccess(false)}
										className="w-full"
									>
										<Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
										{t("auth.forgot_password.try_different")}
									</Button>
								</div>
							</div>
						</Card>

						{/* Back to Home */}
						<div className="text-center mt-6">
							<Button variant="ghost" asChild>
								<Link to="/">
									<Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
									{t("auth.signin.back_to_home")}
								</Link>
							</Button>
						</div>
					</div>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className="p-8 border min-h-full rounded-large bg-gradient-main">
				<div className="max-w-md mx-auto">
					{/* Header */}
					<div className="text-center mb-8">
						<h1 className="text-3xl font-manrope font-semibold mb-2">
							{t("auth.forgot_password.header")}
						</h1>
						<p className="text-muted-foreground">
							{t("auth.forgot_password.subheader")}
						</p>
					</div>

					{/* Forgot Password Form */}
					<Card className="p-6">
						<form onSubmit={handleForgotPassword} className="space-y-4">
							{/* Email Field */}
							<div>
								<Label htmlFor="email">
									{t("auth.forgot_password.email_label")}
								</Label>
								<Input
									id="email"
									name="email"
									type="email"
									placeholder={t("auth.forgot_password.email_placeholder")}
									value={email}
									onChange={handleInputChange}
									required
								/>
							</div>

							{/* Error Message */}
							{error && (
								<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
									{error}
								</div>
							)}

							{/* Submit Button */}
							<Button
								variant="primary"
								type="submit"
								className="w-full"
								disabled={isLoading}
							>
								{isLoading ? (
									<>
										<Icon name="Loader" className="w-4 h-4 mr-2 animate-spin" />
										{t("auth.forgot_password.sending")}
									</>
								) : (
									<>
										<Icon name="Mail" className="w-4 h-4 mr-2" />
										{t("auth.forgot_password.submit")}
									</>
								)}
							</Button>
						</form>
					</Card>

					{/* Back to Sign In */}
					<div className="text-center mt-6">
						<p className="text-muted-foreground">
							{t("auth.signup.already_account")}{" "}
							<Link
								to="/auth/signin"
								className="text-primary font-medium hover:underline"
							>
								{t("auth.signup.signin_link")}
							</Link>
						</p>
					</div>

					{/* Back to Home */}
					<div className="text-center mt-4">
						<Button variant="ghost" asChild>
							<Link to="/">
								<Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
								{t("auth.signin.back_to_home")}
							</Link>
						</Button>
					</div>
				</div>
			</div>
		</Layout>
	);
}
